import type { DeprecatedBadge } from '@coop/components';

import { Button, IconButton } from '@coop/components';
import { ShoppingBasketIcon } from '@coop/icons';
import { useEffect } from 'react';

import { appConfig } from '../../../../../appConfig';
import { LocalStorageFacade } from '../../../../../facades/storage/localStorageFacade';
import { Helpers } from '../../../../../utility/helpers';
import cartItemsSelectors from '../../../../cart/selectors/cartItemsSelectors';
import { useCncFlyInState } from '../../../../common/hooks/useCncFlyInState';
import { useCurrentPriceType } from '../../../../common/hooks/useCurrentPriceType';
import { useAppDispatch, useAppSelector } from '../../../../common/hooks/useThunkDispatch';
import { cartSelectors } from '../../../../common/selectors/cartSelectors';
import tracking from '../../../../common/tracking/tracking';
import { useIsDarkMode } from '../../../../ecommerce/hooks/useIsDarkMode';
import AnimatedBadge from './AnimatedBadge';

const CartTrigger = ({
    badgeColor,
    withTotalPrice,
}: {
    badgeColor: React.ComponentProps<typeof DeprecatedBadge>['color'];
    withTotalPrice: boolean;
}) => {
    const dispatch = useAppDispatch();
    const { initCnc } = useCncFlyInState();
    const postalCode = useAppSelector(cartSelectors.postalCode);

    const openCnc = () => {
        tracking.cnc.openFrom.cartIcon(!!postalCode);
        initCnc();
    };

    const isDarkMode = useIsDarkMode();
    const buttonTheme = isDarkMode ? 'outlined-inverse' : 'outlined-subtle';

    const cartItemCount = useAppSelector(cartItemsSelectors.selectCartQuantity);
    const cartSummary = useAppSelector(cartItemsSelectors.selectCartSummary);
    const { currentPriceType } = useCurrentPriceType();

    const totalPrice = Helpers.toEcommercePrice(
        Helpers.getPrice(cartSummary.totalProductPriceWithDiscounts, currentPriceType),
    );

    useEffect(() => {
        const action = LocalStorageFacade.instance.get('Ecommerce:action');
        if (
            action === 'cnc' &&
            window.location.pathname.indexOf(appConfig.coopSettings.ecommerce.url) > -1
        ) {
            initCnc();
            LocalStorageFacade.instance.remove('Ecommerce:action');
        }
    }, [dispatch, initCnc]);

    return (
        <div style={{ position: 'relative' }}>
            {withTotalPrice ? (
                <Button
                    icon={<ShoppingBasketIcon />}
                    aria-label={(() => {
                        if (cartItemCount > 1) return `Varukorg med ${cartItemCount} varor`;
                        if (cartItemCount === 1) return 'Varukorg med en vara';
                        return 'Varukorg';
                    })()}
                    onClick={openCnc}
                    data-test="showMiniCart"
                    aria-haspopup="dialog"
                    theme={buttonTheme}
                >
                    {withTotalPrice && <>{totalPrice || Helpers.toEcommercePrice(0)} kr</>}
                    <AnimatedBadge
                        color={badgeColor}
                        size="small"
                        wide={cartItemCount > 99}
                        top={-6}
                        right={-6}
                        animationProps={{
                            key: cartItemCount,
                            in: !!cartItemCount,
                            timeout: 500,
                            appear: true,
                            enter: true,
                        }}
                        style={{ wordBreak: 'normal' }}
                        ariaHidden
                    >
                        {cartItemCount}
                    </AnimatedBadge>
                </Button>
            ) : (
                <IconButton
                    icon={<ShoppingBasketIcon />}
                    onClick={openCnc}
                    data-test="showMiniCart"
                    aria-haspopup="dialog"
                    theme={buttonTheme}
                    label={(() => {
                        if (cartItemCount > 1) return `Varukorg med ${cartItemCount} varor`;
                        if (cartItemCount === 1) return 'Varukorg med en vara';
                        return 'Varukorg';
                    })()}
                >
                    <AnimatedBadge
                        color={badgeColor}
                        size="small"
                        wide={cartItemCount > 99}
                        top={-6}
                        right={-6}
                        animationProps={{
                            key: cartItemCount,
                            in: !!cartItemCount,
                            timeout: 500,
                            appear: true,
                            enter: true,
                        }}
                        style={{ wordBreak: 'normal' }}
                        ariaHidden
                    >
                        {cartItemCount}
                    </AnimatedBadge>
                </IconButton>
            )}
        </div>
    );
};

export default CartTrigger;
